/**
 * Worker Registration Wizard
 * Multi-step registration flow combining:
 * 1. Account creation (name, email, password)
 * 2. Personal Info (DOB, nationality, phone, address)
 * 3. Documents (ID, HMS card)
 * 4. Review & Employment Contract
 */
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import ParticleXBackground from "@/components/ParticleXBackground";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { 
  Eye, 
  EyeOff, 
  Mail, 
  User, 
  Phone, 
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  Loader2,
  MapPin,
  Calendar,
  Globe,
  FileText,
  Upload,
  Camera,
  IdCard,
  Shield,
  AlertTriangle,
  X,
  HardHat
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Countries list
const COUNTRIES = [
  { code: "NO", name: "Norway", flag: "🇳🇴" },
  { code: "SE", name: "Sweden", flag: "🇸🇪" },
  { code: "DK", name: "Denmark", flag: "🇩🇰" },
  { code: "FI", name: "Finland", flag: "🇫🇮" },
  { code: "PL", name: "Poland", flag: "🇵🇱" },
  { code: "LT", name: "Lithuania", flag: "🇱🇹" },
  { code: "LV", name: "Latvia", flag: "🇱🇻" },
  { code: "EE", name: "Estonia", flag: "🇪🇪" },
  { code: "RO", name: "Romania", flag: "🇷🇴" },
  { code: "DE", name: "Germany", flag: "🇩🇪" },
  { code: "GB", name: "United Kingdom", flag: "🇬🇧" },
  { code: "NL", name: "Netherlands", flag: "🇳🇱" },
  { code: "UA", name: "Ukraine", flag: "🇺🇦" },
  { code: "OTHER", name: "Other", flag: "🌍" },
];

const WIZARD_STEPS = [
  { id: 1, title: "Konto", titleEn: "Account", icon: User },
  { id: 2, title: "Personalia", titleEn: "Personal Info", icon: IdCard },
  { id: 3, title: "Dokumenter", titleEn: "Documents", icon: FileText },
  { id: 4, title: "Bekreft", titleEn: "Confirm", icon: CheckCircle2 },
];

export default function WorkerRegistrationWizard() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { register } = useAuth();
  const lang = i18n.language === "no" ? "no" : "en";

  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [userToken, setUserToken] = useState(null);
  const [userId, setUserId] = useState(null);

  // Form data for all steps
  const [formData, setFormData] = useState({
    // Step 1 - Account
    name: "",
    email: "",
    password: "",
    phone: "",
    notify_new_projects: true, // Email notifications for new projects
    // Step 2 - Personal Info
    date_of_birth: "",
    nationality: "",
    address: "",
    city: "",
    postal_code: "",
    norwegian_id: "",
    d_number: "",
    emergency_contact_name: "",
    emergency_contact_phone: "",
    // Step 3 - Documents
    documents: [],
  });

  const [errors, setErrors] = useState({});

  // Validation for each step
  const validateStep = (step) => {
    const newErrors = {};
    
    if (step === 1) {
      if (!formData.name.trim()) newErrors.name = t('errors.required') || "Required";
      if (!formData.email.trim()) newErrors.email = t('errors.required') || "Required";
      if (!formData.email.includes("@")) newErrors.email = t('errors.invalidEmail') || "Invalid email";
      if (!formData.password) newErrors.password = t('errors.required') || "Required";
      if (formData.password.length < 6) newErrors.password = t('errors.passwordTooShort') || "Min 6 characters";
      if (!formData.phone.trim()) newErrors.phone = t('errors.required') || "Required";
    }
    
    if (step === 2) {
      if (!formData.date_of_birth) newErrors.date_of_birth = t('errors.required') || "Required";
      if (!formData.nationality) newErrors.nationality = t('errors.required') || "Required";
      if (!formData.address.trim()) newErrors.address = t('errors.required') || "Required";
      if (!formData.city.trim()) newErrors.city = t('errors.required') || "Required";
      if (!formData.postal_code.trim()) newErrors.postal_code = t('errors.required') || "Required";
      // Must have either norwegian_id OR d_number
      if (!formData.norwegian_id.trim() && !formData.d_number.trim()) {
        newErrors.norwegian_id = lang === "no" 
          ? "Fødselsnummer eller D-nummer er påkrevd" 
          : "Norwegian ID or D-number is required";
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  // Step 1: Create Account
  const handleCreateAccount = async () => {
    if (!validateStep(1)) return;
    
    setLoading(true);
    try {
      const user = await register({
        name: formData.name,
        email: formData.email,
        password: formData.password,
        phone: formData.phone,
        role: "worker",
      });
      
      setUserToken(user.token);
      setUserId(user.user_id);
      toast.success(lang === "no" ? "Konto opprettet!" : "Account created!");
      setCurrentStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Save Personal Info
  const handleSavePersonalInfo = async () => {
    if (!validateStep(2)) return;
    
    setLoading(true);
    try {
      await axios.put(`${API}/workers/profile`, {
        date_of_birth: formData.date_of_birth,
        nationality: formData.nationality,
        address: formData.address,
        city: formData.city,
        postal_code: formData.postal_code,
        norwegian_id: formData.norwegian_id,
        d_number: formData.d_number,
        phone: formData.phone,
        emergency_contact_name: formData.emergency_contact_name,
        emergency_contact_phone: formData.emergency_contact_phone,
        notify_new_projects: formData.notify_new_projects,
      }, {
        headers: { Authorization: `Bearer ${userToken}` }
      });
      
      toast.success(lang === "no" ? "Informasjon lagret!" : "Information saved!");
      setCurrentStep(3);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Upload Documents
  const handleDocUpload = async (docType, file) => {
    if (!file) return;
    
    setUploadingDoc(docType);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append("file", file);
      formDataUpload.append("doc_type", docType);
      
      const res = await axios.post(`${API}/workers/documents`, formDataUpload, {
        headers: { 
          Authorization: `Bearer ${userToken}`,
          "Content-Type": "multipart/form-data"
        }
      });
      
      setFormData(prev => ({
        ...prev,
        documents: [...prev.documents, { type: docType, url: res.data.url }]
      }));
      
      toast.success(lang === "no" ? "Dokument lastet opp!" : "Document uploaded!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingDoc(null);
    }
  };

  // Final step: Go to contract
  const handleComplete = () => {
    // Navigate to employment contract page
    navigate("/worker/employment-contract");
  };

  const goBack = () => {
    if (currentStep > 1) {
      // If going back from step 2, we can't un-create the account
      // So just allow navigation for steps 3 and 4
      if (currentStep > 2) {
        setCurrentStep(currentStep - 1);
      }
    }
  };

  const progressPercent = (currentStep / WIZARD_STEPS.length) * 100;

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col">
      <ParticleXBackground color="orange" opacity={0.3} />
      <div className="fixed inset-0 grid-bg opacity-20 pointer-events-none"></div>

      {/* Language Switcher */}
      <div className="fixed top-4 right-4 z-50">
        <LanguageSwitcher />
      </div>

      {/* Header */}
      <div className="relative z-10 px-4 pt-6 pb-4">
        <Link to="/" className="flex items-center gap-1 justify-center mb-6">
          <div className="px-3 py-1.5 bg-[#0A0A0A] border border-[#23F7DD]/50 rounded flex items-center justify-center">
            <span className="text-[#23F7DD] font-bold text-xl font-['Unbounded']">All</span>
          </div>
          <span className="text-2xl font-bold font-['Unbounded'] neon-text-subtle">Stillas</span>
        </Link>

        <h1 className="text-2xl font-['Unbounded'] font-bold text-white text-center mb-2">
          {lang === "no" ? "Bli Stillasmontør" : "Become a Scaffolder"}
        </h1>
        <p className="text-[#94A3B8] text-center text-sm mb-6">
          {lang === "no" 
            ? "Fullfør registreringen i noen få steg" 
            : "Complete your registration in a few steps"}
        </p>

        {/* Progress Bar */}
        <div className="max-w-md mx-auto mb-6">
          <Progress value={progressPercent} className="h-2 bg-white/10" />
          <div className="flex justify-between mt-3">
            {WIZARD_STEPS.map((step) => (
              <div 
                key={step.id} 
                className={`flex flex-col items-center ${
                  step.id <= currentStep ? "text-[#FF5F1F]" : "text-[#64748B]"
                }`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center mb-1 ${
                  step.id < currentStep 
                    ? "bg-[#FF5F1F] text-white" 
                    : step.id === currentStep 
                      ? "bg-[#FF5F1F]/20 border-2 border-[#FF5F1F] text-[#FF5F1F]" 
                      : "bg-white/5 border border-white/20 text-[#64748B]"
                }`}>
                  {step.id < currentStep ? (
                    <CheckCircle2 className="w-4 h-4" />
                  ) : (
                    <step.icon className="w-4 h-4" />
                  )}
                </div>
                <span className="text-xs hidden sm:block">
                  {lang === "no" ? step.title : step.titleEn}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 relative z-10 px-4 pb-8">
        <div className="max-w-md mx-auto">
          <Card className="bg-[#0A0A0A]/80 border-white/10 backdrop-blur-sm">
            <CardContent className="p-6">
              
              {/* Step 1: Account Creation */}
              {currentStep === 1 && (
                <div className="space-y-4">
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-[#FF5F1F]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <User className="w-8 h-8 text-[#FF5F1F]" />
                    </div>
                    <h2 className="text-xl font-bold text-white">
                      {lang === "no" ? "Opprett konto" : "Create Account"}
                    </h2>
                    <p className="text-sm text-[#94A3B8] mt-1">
                      {lang === "no" 
                        ? "Start med grunnleggende informasjon" 
                        : "Start with basic information"}
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <Label className="text-[#94A3B8]">
                        {lang === "no" ? "Fullt navn" : "Full Name"} *
                      </Label>
                      <div className="relative mt-1">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                        <Input
                          value={formData.name}
                          onChange={(e) => handleChange("name", e.target.value)}
                          placeholder={lang === "no" ? "Ola Nordmann" : "John Doe"}
                          className={`pl-10 bg-black/50 border-white/10 text-white ${errors.name ? "border-red-500" : ""}`}
                          data-testid="wizard-name"
                        />
                      </div>
                      {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                    </div>

                    <div>
                      <Label className="text-[#94A3B8]">
                        {lang === "no" ? "E-post" : "Email"} *
                      </Label>
                      <div className="relative mt-1">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                        <Input
                          type="email"
                          value={formData.email}
                          onChange={(e) => handleChange("email", e.target.value)}
                          placeholder="email@example.com"
                          className={`pl-10 bg-black/50 border-white/10 text-white ${errors.email ? "border-red-500" : ""}`}
                          data-testid="wizard-email"
                        />
                      </div>
                      {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                    </div>

                    <div>
                      <Label className="text-[#94A3B8]">
                        {lang === "no" ? "Passord" : "Password"} *
                      </Label>
                      <div className="relative mt-1">
                        <Input
                          type={showPassword ? "text" : "password"}
                          value={formData.password}
                          onChange={(e) => handleChange("password", e.target.value)}
                          placeholder="••••••••"
                          className={`pr-10 bg-black/50 border-white/10 text-white ${errors.password ? "border-red-500" : ""}`}
                          data-testid="wizard-password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-white"
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
                    </div>

                    <div>
                      <Label className="text-[#94A3B8]">
                        {lang === "no" ? "Telefon" : "Phone"} *
                      </Label>
                      <div className="relative mt-1">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                        <Input
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => handleChange("phone", e.target.value)}
                          placeholder="+47 XXX XX XXX"
                          className={`pl-10 bg-black/50 border-white/10 text-white ${errors.phone ? "border-red-500" : ""}`}
                          data-testid="wizard-phone"
                        />
                      </div>
                      {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                    </div>
                  </div>

                  {/* Worker info box */}
                  <div className="p-4 rounded-lg border border-[#FF5F1F]/30 bg-[#FF5F1F]/10 mt-6">
                    <div className="flex items-start gap-3">
                      <HardHat className="w-5 h-5 text-[#FF5F1F] mt-0.5 flex-shrink-0" />
                      <div className="text-sm">
                        <p className="text-white font-medium mb-2">
                          {lang === "no" ? "Som ansatt hos oss får du:" : "As our employee you get:"}
                        </p>
                        <ul className="text-[#94A3B8] space-y-1">
                          <li className="flex items-center gap-2">
                            <CheckCircle2 className="w-3 h-3 text-[#FF5F1F]" />
                            {lang === "no" ? "Lovlige arbeidskontrakter" : "Legal employment contracts"}
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle2 className="w-3 h-3 text-[#FF5F1F]" />
                            {lang === "no" ? "Feriepenger (12%)" : "Holiday pay (12%)"}
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle2 className="w-3 h-3 text-[#FF5F1F]" />
                            {lang === "no" ? "Skattetrekk håndtert" : "Tax handled"}
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle2 className="w-3 h-3 text-[#FF5F1F]" />
                            {lang === "no" ? "Pensjon og forsikring" : "Pension & insurance"}
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Email notifications checkbox */}
                  <div className="flex items-start gap-3 mt-4 p-3 rounded-lg bg-white/5 border border-white/10">
                    <input
                      type="checkbox"
                      id="notify_new_projects"
                      checked={formData.notify_new_projects}
                      onChange={(e) => handleChange("notify_new_projects", e.target.checked)}
                      className="mt-1 w-4 h-4 rounded border-white/30 bg-black/50 text-[#FF5F1F] focus:ring-[#FF5F1F] focus:ring-offset-0"
                    />
                    <label htmlFor="notify_new_projects" className="text-sm text-[#94A3B8] cursor-pointer">
                      <span className="text-white font-medium">
                        {lang === "no" ? "Varsle meg om nye prosjekter" : "Notify me about new projects"}
                      </span>
                      <p className="text-xs mt-1">
                        {lang === "no" 
                          ? "Motta e-post når nye prosjekter blir tilgjengelige for søknad" 
                          : "Receive email when new projects become available for applications"}
                      </p>
                    </label>
                  </div>

                  <Button
                    onClick={handleCreateAccount}
                    disabled={loading}
                    className="w-full bg-[#FF5F1F] hover:bg-[#FF5F1F]/80 text-white mt-6"
                    data-testid="wizard-step1-next"
                  >
                    {loading ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : null}
                    {lang === "no" ? "Neste" : "Next"}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>

                  <p className="text-center text-sm text-[#64748B] mt-4">
                    {lang === "no" ? "Har du allerede konto?" : "Already have an account?"}{" "}
                    <Link to="/login" className="text-[#FF5F1F] hover:underline">
                      {lang === "no" ? "Logg inn" : "Log in"}
                    </Link>
                  </p>
                </div>
              )}

              {/* Step 2: Personal Info */}
              {currentStep === 2 && (
                <div className="space-y-4">
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-[#FF5F1F]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <IdCard className="w-8 h-8 text-[#FF5F1F]" />
                    </div>
                    <h2 className="text-xl font-bold text-white">
                      {lang === "no" ? "Personalia" : "Personal Information"}
                    </h2>
                    <p className="text-sm text-[#94A3B8] mt-1">
                      {lang === "no" 
                        ? "Informasjon som kreves for arbeidskontrakt" 
                        : "Information required for employment contract"}
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-[#94A3B8]">
                        {lang === "no" ? "Fødselsdato" : "Date of Birth"} *
                      </Label>
                      <div className="relative mt-1">
                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                        <Input
                          type="date"
                          value={formData.date_of_birth}
                          onChange={(e) => handleChange("date_of_birth", e.target.value)}
                          className={`pl-10 bg-black/50 border-white/10 text-white ${errors.date_of_birth ? "border-red-500" : ""}`}
                          data-testid="wizard-dob"
                        />
                      </div>
                      {errors.date_of_birth && <p className="text-red-500 text-xs mt-1">{errors.date_of_birth}</p>}
                    </div>

                    <div>
                      <Label className="text-[#94A3B8]">
                        {lang === "no" ? "Nasjonalitet" : "Nationality"} *
                      </Label>
                      <Select
                        value={formData.nationality}
                        onValueChange={(value) => handleChange("nationality", value)}
                      >
                        <SelectTrigger className={`mt-1 bg-black/50 border-white/10 text-white ${errors.nationality ? "border-red-500" : ""}`}>
                          <SelectValue placeholder={lang === "no" ? "Velg land" : "Select country"} />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1A1A1A] border-white/10">
                          {COUNTRIES.map((country) => (
                            <SelectItem key={country.code} value={country.name} className="text-white hover:bg-white/10">
                              <span className="flex items-center gap-2">
                                <span>{country.flag}</span>
                                <span>{country.name}</span>
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.nationality && <p className="text-red-500 text-xs mt-1">{errors.nationality}</p>}
                    </div>
                  </div>

                  {/* ID Numbers */}
                  <div className="p-4 rounded-lg border border-white/10 bg-white/5">
                    <p className="text-sm text-[#94A3B8] mb-3">
                      {lang === "no" 
                        ? "Fyll ut enten fødselsnummer ELLER D-nummer:" 
                        : "Fill in either Norwegian ID OR D-number:"}
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-[#94A3B8] text-sm">
                          {lang === "no" ? "Fødselsnummer (11 siffer)" : "Norwegian ID (11 digits)"}
                        </Label>
                        <Input
                          value={formData.norwegian_id}
                          onChange={(e) => handleChange("norwegian_id", e.target.value)}
                          placeholder="12345678901"
                          maxLength={11}
                          className={`mt-1 bg-black/50 border-white/10 text-white ${errors.norwegian_id ? "border-red-500" : ""}`}
                          data-testid="wizard-norwegian-id"
                        />
                      </div>
                      <div>
                        <Label className="text-[#94A3B8] text-sm">
                          {lang === "no" ? "D-nummer (11 siffer)" : "D-number (11 digits)"}
                        </Label>
                        <Input
                          value={formData.d_number}
                          onChange={(e) => handleChange("d_number", e.target.value)}
                          placeholder="12345678901"
                          maxLength={11}
                          className="mt-1 bg-black/50 border-white/10 text-white"
                          data-testid="wizard-d-number"
                        />
                      </div>
                    </div>
                    {errors.norwegian_id && <p className="text-red-500 text-xs mt-2">{errors.norwegian_id}</p>}
                  </div>

                  {/* Address */}
                  <div>
                    <Label className="text-[#94A3B8]">
                      {lang === "no" ? "Adresse" : "Address"} *
                    </Label>
                    <div className="relative mt-1">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                      <Input
                        value={formData.address}
                        onChange={(e) => handleChange("address", e.target.value)}
                        placeholder={lang === "no" ? "Gateadresse 123" : "Street Address 123"}
                        className={`pl-10 bg-black/50 border-white/10 text-white ${errors.address ? "border-red-500" : ""}`}
                        data-testid="wizard-address"
                      />
                    </div>
                    {errors.address && <p className="text-red-500 text-xs mt-1">{errors.address}</p>}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-[#94A3B8]">
                        {lang === "no" ? "Postnummer" : "Postal Code"} *
                      </Label>
                      <Input
                        value={formData.postal_code}
                        onChange={(e) => handleChange("postal_code", e.target.value)}
                        placeholder="0123"
                        className={`mt-1 bg-black/50 border-white/10 text-white ${errors.postal_code ? "border-red-500" : ""}`}
                        data-testid="wizard-postal"
                      />
                      {errors.postal_code && <p className="text-red-500 text-xs mt-1">{errors.postal_code}</p>}
                    </div>
                    <div>
                      <Label className="text-[#94A3B8]">
                        {lang === "no" ? "By" : "City"} *
                      </Label>
                      <Input
                        value={formData.city}
                        onChange={(e) => handleChange("city", e.target.value)}
                        placeholder="Oslo"
                        className={`mt-1 bg-black/50 border-white/10 text-white ${errors.city ? "border-red-500" : ""}`}
                        data-testid="wizard-city"
                      />
                      {errors.city && <p className="text-red-500 text-xs mt-1">{errors.city}</p>}
                    </div>
                  </div>

                  {/* Emergency Contact */}
                  <div className="p-4 rounded-lg border border-white/10 bg-white/5">
                    <p className="text-sm text-white font-medium mb-3 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-yellow-500" />
                      {lang === "no" ? "Nødkontakt (valgfritt)" : "Emergency Contact (optional)"}
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-[#94A3B8] text-sm">
                          {lang === "no" ? "Navn" : "Name"}
                        </Label>
                        <Input
                          value={formData.emergency_contact_name}
                          onChange={(e) => handleChange("emergency_contact_name", e.target.value)}
                          className="mt-1 bg-black/50 border-white/10 text-white"
                        />
                      </div>
                      <div>
                        <Label className="text-[#94A3B8] text-sm">
                          {lang === "no" ? "Telefon" : "Phone"}
                        </Label>
                        <Input
                          value={formData.emergency_contact_phone}
                          onChange={(e) => handleChange("emergency_contact_phone", e.target.value)}
                          className="mt-1 bg-black/50 border-white/10 text-white"
                        />
                      </div>
                    </div>
                  </div>

                  <Button
                    onClick={handleSavePersonalInfo}
                    disabled={loading}
                    className="w-full bg-[#FF5F1F] hover:bg-[#FF5F1F]/80 text-white mt-4"
                    data-testid="wizard-step2-next"
                  >
                    {loading ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : null}
                    {lang === "no" ? "Neste" : "Next"}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              )}

              {/* Step 3: Documents */}
              {currentStep === 3 && (
                <div className="space-y-4">
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-[#FF5F1F]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <FileText className="w-8 h-8 text-[#FF5F1F]" />
                    </div>
                    <h2 className="text-xl font-bold text-white">
                      {lang === "no" ? "Dokumenter" : "Documents"}
                    </h2>
                    <p className="text-sm text-[#94A3B8] mt-1">
                      {lang === "no" 
                        ? "Last opp ID-dokumenter (valgfritt nå, kan gjøres senere)" 
                        : "Upload ID documents (optional now, can be done later)"}
                    </p>
                  </div>

                  {/* ID Document */}
                  <div className="p-4 rounded-lg border border-white/10 bg-white/5">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <IdCard className="w-5 h-5 text-[#FF5F1F]" />
                        <span className="text-white font-medium">
                          {lang === "no" ? "Pass / ID-kort" : "Passport / ID Card"}
                        </span>
                      </div>
                      {formData.documents.find(d => d.type === "passport") ? (
                        <CheckCircle2 className="w-5 h-5 text-green-500" />
                      ) : null}
                    </div>
                    <p className="text-sm text-[#94A3B8] mb-3">
                      {lang === "no" 
                        ? "Last opp forsiden av passet eller ID-kortet ditt" 
                        : "Upload the front of your passport or ID card"}
                    </p>
                    <label className="flex items-center justify-center gap-2 py-3 px-4 border border-dashed border-white/20 rounded-lg cursor-pointer hover:border-[#FF5F1F]/50 transition-colors">
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleDocUpload("passport", e.target.files[0])}
                        disabled={uploadingDoc === "passport"}
                      />
                      {uploadingDoc === "passport" ? (
                        <Loader2 className="w-4 h-4 animate-spin text-[#FF5F1F]" />
                      ) : (
                        <Upload className="w-4 h-4 text-[#64748B]" />
                      )}
                      <span className="text-sm text-[#94A3B8]">
                        {formData.documents.find(d => d.type === "passport") 
                          ? (lang === "no" ? "Lastet opp ✓" : "Uploaded ✓")
                          : (lang === "no" ? "Velg fil" : "Choose file")}
                      </span>
                    </label>
                  </div>

                  {/* HMS Card */}
                  <div className="p-4 rounded-lg border border-white/10 bg-white/5">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <Shield className="w-5 h-5 text-[#FF5F1F]" />
                        <span className="text-white font-medium">
                          {lang === "no" ? "HMS-kort" : "HMS Card"}
                        </span>
                      </div>
                      {formData.documents.find(d => d.type === "hms_card") ? (
                        <CheckCircle2 className="w-5 h-5 text-green-500" />
                      ) : null}
                    </div>
                    <p className="text-sm text-[#94A3B8] mb-3">
                      {lang === "no" 
                        ? "HMS-kort for byggebransjen (hvis du har)" 
                        : "HMS card for construction (if you have one)"}
                    </p>
                    <label className="flex items-center justify-center gap-2 py-3 px-4 border border-dashed border-white/20 rounded-lg cursor-pointer hover:border-[#FF5F1F]/50 transition-colors">
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleDocUpload("hms_card", e.target.files[0])}
                        disabled={uploadingDoc === "hms_card"}
                      />
                      {uploadingDoc === "hms_card" ? (
                        <Loader2 className="w-4 h-4 animate-spin text-[#FF5F1F]" />
                      ) : (
                        <Upload className="w-4 h-4 text-[#64748B]" />
                      )}
                      <span className="text-sm text-[#94A3B8]">
                        {formData.documents.find(d => d.type === "hms_card") 
                          ? (lang === "no" ? "Lastet opp ✓" : "Uploaded ✓")
                          : (lang === "no" ? "Velg fil" : "Choose file")}
                      </span>
                    </label>
                  </div>

                  <div className="p-4 rounded-lg border border-yellow-500/30 bg-yellow-500/10">
                    <p className="text-sm text-[#94A3B8]">
                      <AlertTriangle className="w-4 h-4 text-yellow-500 inline mr-2" />
                      {lang === "no" 
                        ? "Du kan laste opp dokumenter nå eller senere fra profilen din." 
                        : "You can upload documents now or later from your profile."}
                    </p>
                  </div>

                  <div className="flex gap-3 mt-6">
                    <Button
                      variant="outline"
                      onClick={goBack}
                      className="flex-1 border-white/20 text-white hover:bg-white/5"
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      {lang === "no" ? "Tilbake" : "Back"}
                    </Button>
                    <Button
                      onClick={() => setCurrentStep(4)}
                      className="flex-1 bg-[#FF5F1F] hover:bg-[#FF5F1F]/80 text-white"
                      data-testid="wizard-step3-next"
                    >
                      {lang === "no" ? "Neste" : "Next"}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </div>
              )}

              {/* Step 4: Review & Complete */}
              {currentStep === 4 && (
                <div className="space-y-4">
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle2 className="w-8 h-8 text-green-500" />
                    </div>
                    <h2 className="text-xl font-bold text-white">
                      {lang === "no" ? "Nesten ferdig!" : "Almost Done!"}
                    </h2>
                    <p className="text-sm text-[#94A3B8] mt-1">
                      {lang === "no" 
                        ? "Gjennomgå informasjonen og fullfør registreringen" 
                        : "Review your information and complete registration"}
                    </p>
                  </div>

                  {/* Summary */}
                  <div className="space-y-3">
                    <div className="p-4 rounded-lg border border-white/10 bg-white/5">
                      <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                        <User className="w-4 h-4 text-[#FF5F1F]" />
                        {lang === "no" ? "Kontoinformasjon" : "Account Information"}
                      </h3>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="text-[#64748B]">{lang === "no" ? "Navn:" : "Name:"}</div>
                        <div className="text-white">{formData.name}</div>
                        <div className="text-[#64748B]">{lang === "no" ? "E-post:" : "Email:"}</div>
                        <div className="text-white">{formData.email}</div>
                        <div className="text-[#64748B]">{lang === "no" ? "Telefon:" : "Phone:"}</div>
                        <div className="text-white">{formData.phone}</div>
                      </div>
                    </div>

                    <div className="p-4 rounded-lg border border-white/10 bg-white/5">
                      <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                        <IdCard className="w-4 h-4 text-[#FF5F1F]" />
                        {lang === "no" ? "Personalia" : "Personal Details"}
                      </h3>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="text-[#64748B]">{lang === "no" ? "Fødselsdato:" : "DOB:"}</div>
                        <div className="text-white">{formData.date_of_birth || "-"}</div>
                        <div className="text-[#64748B]">{lang === "no" ? "Nasjonalitet:" : "Nationality:"}</div>
                        <div className="text-white">{formData.nationality || "-"}</div>
                        <div className="text-[#64748B]">{lang === "no" ? "Adresse:" : "Address:"}</div>
                        <div className="text-white">{formData.address}, {formData.postal_code} {formData.city}</div>
                        {formData.norwegian_id && (
                          <>
                            <div className="text-[#64748B]">{lang === "no" ? "Fødselsnr:" : "Norwegian ID:"}</div>
                            <div className="text-white">***{formData.norwegian_id.slice(-4)}</div>
                          </>
                        )}
                        {formData.d_number && (
                          <>
                            <div className="text-[#64748B]">D-nummer:</div>
                            <div className="text-white">***{formData.d_number.slice(-4)}</div>
                          </>
                        )}
                      </div>
                    </div>

                    <div className="p-4 rounded-lg border border-white/10 bg-white/5">
                      <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                        <FileText className="w-4 h-4 text-[#FF5F1F]" />
                        {lang === "no" ? "Dokumenter" : "Documents"}
                      </h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center justify-between">
                          <span className="text-[#64748B]">{lang === "no" ? "Pass/ID:" : "Passport/ID:"}</span>
                          {formData.documents.find(d => d.type === "passport") ? (
                            <span className="text-green-500 flex items-center gap-1">
                              <CheckCircle2 className="w-3 h-3" />
                              {lang === "no" ? "Lastet opp" : "Uploaded"}
                            </span>
                          ) : (
                            <span className="text-yellow-500">{lang === "no" ? "Mangler" : "Missing"}</span>
                          )}
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-[#64748B]">HMS-kort:</span>
                          {formData.documents.find(d => d.type === "hms_card") ? (
                            <span className="text-green-500 flex items-center gap-1">
                              <CheckCircle2 className="w-3 h-3" />
                              {lang === "no" ? "Lastet opp" : "Uploaded"}
                            </span>
                          ) : (
                            <span className="text-yellow-500">{lang === "no" ? "Mangler" : "Missing"}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Next Steps */}
                  <div className="p-4 rounded-lg border border-[#FF5F1F]/30 bg-[#FF5F1F]/10">
                    <h3 className="text-white font-medium mb-2">
                      {lang === "no" ? "Neste steg:" : "Next Steps:"}
                    </h3>
                    <ol className="text-sm text-[#94A3B8] space-y-2 list-decimal list-inside">
                      <li>{lang === "no" ? "Les og signer arbeidskontrakten" : "Read and sign the employment contract"}</li>
                      <li>{lang === "no" ? "Vent på godkjenning fra HR" : "Wait for HR approval"}</li>
                      <li>{lang === "no" ? "Begynn å søke på prosjekter!" : "Start applying for projects!"}</li>
                    </ol>
                  </div>

                  <div className="flex gap-3 mt-6">
                    <Button
                      variant="outline"
                      onClick={goBack}
                      className="flex-1 border-white/20 text-white hover:bg-white/5"
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      {lang === "no" ? "Tilbake" : "Back"}
                    </Button>
                    <Button
                      onClick={handleComplete}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                      data-testid="wizard-complete"
                    >
                      {lang === "no" ? "Gå til kontrakt" : "Go to Contract"}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </div>
              )}

            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
